import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { CatalogItem, CategoryRow } from '../api/types';
import { Badge, TypeBadge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { EmptyState, ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Checkbox, Field, Input, Select } from '../components/Form';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, money, num } from '../lib/format';

export function CatalogPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [categoryId, setCategoryId] = useState('');
  const [type, setType] = useState('');
  const [inStock, setInStock] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [renaming, setRenaming] = useState<CatalogItem | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (categoryId) params.set('category_id', categoryId);
    if (type) params.set('type', type);
    if (inStock) params.set('in_stock', '1');
    params.set('include_disabled', includeDisabled ? '1' : '0');
    if (search) params.set('search', search);
    return `/admin-api/catalog/projects?${params.toString()}`;
  }, [page, limit, categoryId, type, inStock, includeDisabled, search]);

  const listQuery = useQuery<{ list: CatalogItem[]; total: number; freshness: { projects: number; stale: number; lastSyncedAt: string | null } }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
    // While a background sync runs, keep pulling fresh data so the table updates itself.
    refetchInterval: syncing ? 4000 : false,
  });

  const categoriesQuery = useQuery<{ list: CategoryRow[] }, ApiError>({
    queryKey: ['api', '/admin-api/catalog/categories'],
    queryFn: ({ signal }) => api.get('/admin-api/catalog/categories', signal),
    retry: 1,
  });

  const saveRename = async () => {
    if (!renaming) return;
    const value = renameValue.trim();
    try {
      await api.patch(`/admin-api/catalog/projects/${renaming.id}`, { display_name: value || null });
      toast.success(value ? 'Название сохранено' : 'Название сброшено на автоматическое');
      setRenaming(null);
      invalidatePrefix(queryClient, '/admin-api/catalog');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const toggleEnabled = async (item: CatalogItem, enabled: boolean) => {
    try {
      await api.patch(`/admin-api/catalog/projects/${item.id}`, { enabled });
      toast.success(enabled ? 'Включено' : 'Выключено');
      void invalidatePrefix(queryClient, '/admin-api/catalog');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const bulkToggle = async (enabled: boolean) => {
    if (selected.size === 0) {
      toast.info('Выберите проекты');
      return;
    }
    const ok = await confirm({
      title: enabled ? 'Включить выбранные?' : 'Выключить выбранные?',
      body: `${selected.size} проектов.`,
      confirmLabel: enabled ? 'Включить' : 'Выключить',
    });
    if (!ok) return;
    try {
      await api.post('/admin-api/catalog/projects/bulk', { ids: [...selected], enabled });
      toast.success(`${selected.size} проектов обновлено`);
      setSelected(new Set());
      void invalidatePrefix(queryClient, '/admin-api/catalog');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const sync = async () => {
    const ok = await confirm({
      title: 'Синхронизировать каталог?',
      body: 'Операция идёт в фоне (~20-40 секунд): запрос данных у upstream и обновление проектов всех типов.',
      confirmLabel: 'Синхронизировать',
    });
    if (!ok) return;
    setSyncing(true);
    try {
      const result = await api.post<{ started: boolean; alreadyRunning?: boolean }>('/admin-api/catalog/sync');
      if (result.alreadyRunning) {
        toast.info('Синхронизация уже идёт — подождите завершения');
      } else {
        toast.success('Синхронизация запущена, таблица обновится автоматически');
      }
      // The sync runs server-side in the background; keep the button spinning and
      // let refetchInterval pick up fresh rows until it is done.
      window.setTimeout(() => setSyncing(false), 40_000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка синхронизации');
      setSyncing(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const list = listQuery.data?.list ?? [];
  const total = listQuery.data?.total ?? 0;
  const freshness = listQuery.data?.freshness;
  const categories = categoriesQuery.data?.list ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Каталог"
        description={freshness ? `Проектов: ${num(freshness.projects)} · устаревших: ${num(freshness.stale)} · синхр.: ${ago(freshness.lastSyncedAt)}` : 'Загрузка…'}
        actions={<Button variant="primary" loading={syncing} icon={<RefreshCw className="size-4" />} onClick={sync}>Синхронизировать каталог</Button>}
      />

      <Card title="Фильтры" bodyClassName="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Категория">
            <Select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}>
              <option value="">Все</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name} ({cat.projects})</option>
              ))}
            </Select>
          </Field>
          <Field label="Тип">
            <Select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
              <option value="">Все</option>
              <option value="1">SMS</option>
              <option value="2">Аренда</option>
              <option value="3">Спец.</option>
            </Select>
          </Field>
          <Field label="Поиск">
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(e.currentTarget.value.trim()); setPage(1); } }} placeholder="название" />
          </Field>
          <div className="flex items-center gap-4">
            <Checkbox checked={inStock} onChange={setInStock} label="Только в наличии" />
            <Checkbox checked={includeDisabled} onChange={setIncludeDisabled} label="Вкл. отключённые" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>Найти</Button>
            <Button variant="ghost" onClick={() => { setCategoryId(''); setType(''); setInStock(false); setSearch(''); setSearchInput(''); setPage(1); }}>Сбросить</Button>
          </div>
        </div>
      </Card>

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-900/60 bg-sky-950/40 p-3">
          <span className="text-sm text-sky-200">Выбрано: {selected.size}</span>
          <Button size="sm" variant="success" onClick={() => bulkToggle(true)}>Включить</Button>
          <Button size="sm" variant="danger" onClick={() => bulkToggle(false)}>Выключить</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Снять выделение</Button>
        </div>
      ) : null}

      <Card noPadding>
        {listQuery.error ? (
          <div className="p-3"><ErrorBanner message={listQuery.error.message} onRetry={() => listQuery.refetch()} /></div>
        ) : listQuery.isLoading && !list.length ? (
          <LoadingBlock />
        ) : list.length === 0 ? (
          <EmptyState title="Проекты не найдены" />
        ) : (
          <>
            <DataTable<CatalogItem>
              columns={[
                { key: 'select', header: '', width: '2.5rem', render: (r) => (
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="size-4 rounded border-zinc-600 bg-zinc-900 accent-sky-500" />
                ) },
                { key: 'name', header: 'Название', render: (r) => (
                  <div className="flex items-center gap-2">
                    <TypeBadge type={r.type} />
                    <span className="truncate">{r.name}</span>
                  </div>
                ) },
                { key: 'category', header: 'Категория', hide: 'md', render: (r) => <span className="truncate">{r.category ?? '—'}</span> },
                { key: 'stock', header: 'Запас', align: 'right', width: '5rem', render: (r) => <Mono>{num(r.stock)}</Mono> },
                { key: 'cost', header: 'Себест.', align: 'right', hide: 'lg', render: (r) => money(r.cost) },
                { key: 'price', header: 'Цена', align: 'right', render: (r) => money(r.price) },
                { key: 'enabled', header: 'Статус', width: '5rem', align: 'center', render: (r) =>
                  r.enabled !== undefined ? <Badge tone={r.enabled ? 'success' : 'muted'}>{r.enabled ? 'вкл' : 'выкл'}</Badge> : '—',
                },
                { key: 'actions', header: '', width: '11rem', align: 'right', render: (r) => (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setRenaming(r); setRenameValue(r.name_original === r.name ? '' : r.name); }}>
                      Название
                    </Button>
                    {r.enabled !== undefined && (
                      <Button size="sm" variant="ghost" onClick={() => toggleEnabled(r, !r.enabled)}>
                        {r.enabled ? 'Выключить' : 'Включить'}
                      </Button>
                    )}
                  </div>
                ) },
              ]}
              rows={list}
              rowKey={(r) => r.id}
              loading={listQuery.isFetching}
              minWidth="36rem"
            />
            <div className="border-t border-zinc-800 px-3 py-2 sm:px-4">
              <Pagination page={page} limit={limit} total={total} count={list.length} onPage={setPage} onLimit={(v) => { setLimit(v); setPage(1); }} limits={[50, 100, 200, 500]} />
            </div>
          </>
        )}
      </Card>

      <CountriesCard />

      <Modal
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        title="Как отдавать этот сервис"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenaming(null)}>Отмена</Button>
            <Button variant="primary" onClick={() => void saveRename()}>Сохранить</Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            Это название уйдёт клиентам в ответе API вместо оригинального. Пустое
            поле возвращает автоматический перевод.
          </p>
          <Field label="Оригинал у поставщика">
            <Input value={renaming?.name_original ?? ''} readOnly />
          </Field>
          <Field label="Название на отдачу">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Например: Google"
              onKeyDown={(e) => e.key === 'Enter' && void saveRename()}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

// ------------------------------------------------------------------- страны

type AdminCategory = {
  id: number;
  upstream_id: number;
  name: string;
  name_original: string;
  name_en: string | null;
  display_name: string | null;
  iso2: string | null;
  enabled: boolean;
  projects: number;
};

/** "US" → 🇺🇸: региональные индикаторы, картинки флагов не нужны. */
function flagOf(iso2: string | null): string {
  const code = (iso2 ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏳️';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function CountriesCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery<{ list: AdminCategory[] }, ApiError>({
    queryKey: ['api', '/admin-api/catalog/categories'],
    queryFn: ({ signal }) => api.get<{ list: AdminCategory[] }>('/admin-api/catalog/categories', signal),
    retry: 1,
  });
  const [draft, setDraft] = useState<Record<number, { name: string; iso2: string }>>({});

  const rows = query.data?.list ?? [];
  const valueOf = (c: AdminCategory) => draft[c.upstream_id] ?? { name: c.display_name ?? '', iso2: c.iso2 ?? '' };

  const save = async (c: AdminCategory) => {
    const v = valueOf(c);
    const iso = v.iso2.trim().toUpperCase();
    if (iso && !/^[A-Z]{2}$/.test(iso)) {
      toast.error('Код страны — ровно две буквы, например US');
      return;
    }
    try {
      await api.patch(`/admin-api/catalog/categories/${c.upstream_id}`, {
        display_name: v.name.trim() || null,
        iso2: iso || null,
      });
      toast.success('Страна сохранена');
      setDraft((d) => { const next = { ...d }; delete next[c.upstream_id]; return next; });
      invalidatePrefix(queryClient, '/admin-api/catalog');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const toggle = async (c: AdminCategory) => {
    try {
      await api.patch(`/admin-api/catalog/categories/${c.upstream_id}`, { enabled: !c.enabled });
      invalidatePrefix(queryClient, '/admin-api/catalog');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  return (
    <Card title="Страны — как отдавать клиентам">
      {query.isError && <ErrorBanner message={query.error.message} onRetry={() => query.refetch()} />}
      {!query.data ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-2 p-3 sm:p-4">
          <p className="text-sm text-zinc-400">
            Название уходит клиентам вместо оригинального, код страны — чтобы они
            рисовали флаг. Пустое название возвращает автоматический перевод.
          </p>
          {rows.map((c) => {
            const v = valueOf(c);
            const dirty = draft[c.upstream_id] !== undefined;
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 px-2 py-2">
                <span className="w-6 text-center text-lg">{flagOf(v.iso2 || c.iso2)}</span>
                <span className="w-48 shrink-0 truncate text-sm text-zinc-400" title={c.name_original}>
                  {c.name_en ?? c.name_original}
                </span>
                <Input
                  className="w-52"
                  value={v.name}
                  placeholder={c.name_en ?? c.name_original}
                  onChange={(e) => setDraft((d) => ({ ...d, [c.upstream_id]: { ...v, name: e.target.value } }))}
                />
                <Input
                  className="w-20"
                  value={v.iso2}
                  placeholder="US"
                  maxLength={2}
                  onChange={(e) => setDraft((d) => ({ ...d, [c.upstream_id]: { ...v, iso2: e.target.value } }))}
                />
                <span className="text-xs text-zinc-500">{c.projects} сервисов</span>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant={dirty ? 'primary' : 'ghost'} disabled={!dirty} onClick={() => void save(c)}>
                    Сохранить
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void toggle(c)}>
                    {c.enabled ? 'Выключить' : 'Включить'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
